import { useEffect, useRef } from 'react';

import { fetchWhoami, isForbiddenTenantError, probeBaseUrl } from '../api/whoami';
import { PROBE_SETTLE_TIMEOUT_MS, useEnvironment } from '../context/EnvironmentContext';

// FD-422 / FD-ADR-040 §8 — the eager boot probe.
//
// Asks every environment in the fleet "may I?" once, so the MSP persona's
// landing experience is the answer rather than something discovered by clicking
// into an environment they were never permitted to see. It also collects each
// Manager's own `ENVIRONMENT_NAME`, which is the drift banner's only input.
//
// THREE PROPERTIES, each of which is a decision rather than an implementation
// detail.
//
// 1. IT NEVER BLOCKS THE SHELL, AND SINCE FD-586 IT BOUNDS THE ROUTED PAGE. It is
//    an effect on a component that renders null; the rail, the top bar and the
//    selector all paint while the probes are in flight. What waits is the routed
//    page of a MULTI-environment console, and only until the ACTIVE environment's
//    probe settles (`environmentResolved`, read by `AppShell`). FD-422 rejected
//    that wait as "a guaranteed landing delay for a marginal improvement in a rare
//    case" — and on the Air Bridge fleet the case stopped being rare: every
//    customer admin landed on Rubix's environment and was greeted by a refusal.
//    The wait is bounded by `PROBE_SETTLE_TIMEOUT_MS` (below), so a probe that
//    never answers costs a delay, never a hung console.
//
// 2. A FAILURE DEGRADES TO "STILL LISTED", NOT TO HIDDEN. Only a
//    `403 FORBIDDEN_TENANT` makes an environment forbidden; a 500, a timeout, a
//    CORS failure and a Manager that is simply down all leave it `unknown` — now
//    marked SETTLED, so the landing gate stops waiting on it, but never hidden.
//    Failing the other way would mean one flaky network moment empties the
//    selector — a console that looks like a total loss of access when nothing is
//    wrong with access at all, and for Rubix staff the loss of the one signal that
//    tells a DOWN deployment from a forbidden one.
//
// 3. IT MOUNTS INSIDE `AuthenticatedTemplate`. `AppShell` renders only for a
//    signed-in operator, so `authFetchAt`'s token acquisition always has an
//    account. Probing above that boundary would throw "No authenticated account"
//    on every environment and record nothing.

/**
 * Runs the probes. Renders nothing.
 *
 * Mounted in `AppShell` rather than in `EnvironmentProvider` for a structural
 * reason: the provider sits above `MsalProvider`'s authenticated subtree and is
 * rendered by several tests that have no MSAL instance at all. Keeping the probe
 * a separate component keeps the provider free of an auth dependency and makes
 * the probe independently testable.
 */
export default function EnvironmentProbe() {
  const { environments, recordEnvironmentProbe, markEnvironmentsSettled } = useEnvironment();

  // Which environments have been ASKED — not which have answered. Survives the
  // effect, so React 18 StrictMode's mount → unmount → mount cycle issues each
  // probe exactly once.
  //
  // Deliberately NOT paired with a cancellation flag. The obvious shape —
  // `let cancelled = false` with a cleanup that sets it — is wrong here and
  // silently so: StrictMode's cleanup would cancel the first run's in-flight
  // promises while this ref makes the second run skip them, so nothing would
  // ever be recorded in development and everything would work in production.
  // Recording state after unmount is a no-op in React 18, so there is nothing to
  // guard against.
  const asked = useRef(new Set<string>());

  useEffect(() => {
    for (const environment of environments) {
      if (asked.current.has(environment.id)) continue;
      asked.current.add(environment.id);

      void fetchWhoami(probeBaseUrl(environment))
        .then((who) => {
          recordEnvironmentProbe(environment.id, {
            reachability: 'reachable',
            environmentName: who.environmentName ?? null,
            // FD-586 — the label only a permitted tenant can read. Resolution
            // (config wins, then this, then the id) happens once, in the provider.
            environmentLabel: who.environmentLabel ?? null,
            // FD-594 — the wizard reads this to decide whether a Hybrid campaign
            // may default to the deployment's own tenant. Presentation only.
            adminAuthMode: who.adminAuthMode ?? null,
          });
        })
        .catch((error: unknown) => {
          if (!isForbiddenTenantError(error)) {
            // Everything that is not a tenant refusal stays `unknown` — but
            // SETTLED (FD-586), so the landing gate stops waiting on it. See
            // property 2 above: this branch is the whole fail-open posture, and
            // the operator learns an environment is broken by using it, which is
            // a better signal than hiding it as though access were the problem.
            markEnvironmentsSettled([environment.id]);
            return;
          }
          recordEnvironmentProbe(environment.id, {
            reachability: 'forbidden',
            environmentName: null,
          });
        });
    }
  }, [environments, recordEnvironmentProbe, markEnvironmentsSettled]);

  // FD-586 — the bound on the landing gate. Past it, every environment still
  // unanswered is treated as settled-unknown and the page renders; a late answer
  // still lands (a `recordEnvironmentProbe` overwrites, `markEnvironmentsSettled`
  // never does), so a slow Manager that eventually refuses is still hidden.
  //
  // Keyed on the id LIST, not on `environments`: the provider rebuilds that array
  // whenever a probe answers (labels resolve from health), and an effect keyed on
  // it would restart the clock on every answer — a timeout that never fires.
  //
  // Not started for a single-environment console. There is no landing gate there
  // (Mode 1 never waits on a probe), so a timer would be a state write with no
  // reader.
  const idsKey = environments.map((environment) => environment.id).join('\u0000');
  const multi = environments.length > 1;
  useEffect(() => {
    if (!multi) return undefined;
    const ids = idsKey.split('\u0000');
    const timer = setTimeout(() => markEnvironmentsSettled(ids), PROBE_SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [idsKey, multi, markEnvironmentsSettled]);

  return null;
}
